import React, { useState, useCallback, useRef, useEffect } from 'react';

const DEFAULT_PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
      <rect width="400" height="400" fill="#F3F4F6"/>
      <path d="M175 180a25 25 0 1 0 50 0 25 25 0 0 0-50 0z" fill="#D1D5DB"/>
      <path d="M140 280l60-80 40 53 30-20 50 47H140z" fill="#D1D5DB"/>
    </svg>`
  );

interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** URL to use when the primary src fails to load */
  fallbackSrc?: string;
  /** Render initials instead of image on error (e.g. "AB") */
  initials?: string;
  /** Background gradient classes applied to the initials avatar */
  initialsClassName?: string;
  /** Set fetchpriority attribute on the img element (React 18 compatible) */
  fetchPriority?: 'high' | 'low' | 'auto';
}

/**
 * Drop-in `<img>` replacement with automatic error fallback.
 *
 * Usage:
 * ```tsx
 * <SafeImage src={user.avatar} alt="Avatar" fallbackSrc="/placeholder-avatar.png" />
 * <SafeImage src={provider.logo} initials="AB" className="w-12 h-12 rounded-full" />
 * ```
 */
const SafeImage: React.FC<SafeImageProps> = ({
  src,
  fallbackSrc,
  initials,
  initialsClassName = 'bg-gradient-to-br from-primary-600 to-accent-600',
  alt = '',
  className = '',
  fetchPriority,
  ...rest
}) => {
  const [errored, setErrored] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleError = useCallback(() => setErrored(true), []);

  // Apply fetchpriority via DOM attribute (React 18 doesn't support it natively)
  useEffect(() => {
    if (fetchPriority && imgRef.current) {
      imgRef.current.setAttribute('fetchpriority', fetchPriority);
    }
  }, [fetchPriority]);

  // When errored and initials provided, render a styled avatar
  if (errored && initials) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={`flex items-center justify-center text-white font-semibold select-none ${initialsClassName} ${className}`}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={errored ? (fallbackSrc || DEFAULT_PLACEHOLDER) : (src || fallbackSrc || DEFAULT_PLACEHOLDER)}
      alt={alt}
      className={className}
      onError={handleError}
      loading={rest.loading || 'lazy'}
      {...rest}
    />
  );
};

export default SafeImage;
